import { Component, OnInit, PLATFORM_ID, inject, signal, computed, ViewEncapsulation } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router, ActivatedRoute, RouterLink } from "@angular/router";
import { MessageService, ConfirmationService, SelectItem, PrimeTemplate } from "primeng/api";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  switchMap,
  tap,
} from "rxjs";
import { PrimeNG } from "primeng/config";
import { withClientLabel } from "src/app/common/client-label";
import { AbstractFormComponent } from "src/app/common/abstract-edit.component";
import {
  BOOKINGS_TYPE,
  LOCATIONS_LIST,
  NUMBER_LIST,
  NUMBER_LIST_CHILDREN,
  PARAM_BOOKINGS_PATH,
  STATE_LIST,
} from "src/app/constants/constants";
import { Bookings } from "src/app/models/bookings";
import { BookingsService } from "src/app/services/bookings.service";
import { Clients } from "src/app/models/clients";
import { EmployeesService } from "src/app/services/employees.service";
import { Employees } from "src/app/models/employees";
import { Category } from "src/app/models/category";
import { ClientsService } from "src/app/services/clients.service";
import { Rooms } from "src/app/models/rooms";
import { RoomsService } from "src/app/services/rooms.service";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { TopNavComponent } from "../../top-nav/top-nav.component";
import { Card } from "primeng/card";
import { ButtonDirective } from "primeng/button";
import { Message } from "primeng/message";
import { SelectButton } from "primeng/selectbutton";
import { DatePicker } from "primeng/datepicker";
import { Select } from "primeng/select";
import { InputText } from "primeng/inputtext";
import { AutoComplete } from "primeng/autocomplete";
import { Textarea } from "primeng/textarea";
import { ClientsFormDetailComponent } from "../../components/clients-form-detail/clients-form-detail.component";
import { ConfirmDialog } from "primeng/confirmdialog";
import { Toast } from "primeng/toast";
import { Drawer } from "primeng/drawer";
import { OnlyCalendarComponent } from "../../components/calendar/calendar.component";
import { FormRowComponent } from "../../components/form-row/form-row.component";
import { ChildAgesComponent } from "../../components/child-ages/child-ages.component";
import { StatBarComponent, Stat } from "../../components/stat-bar/stat-bar.component";
import { PriceBreakdownComponent } from "../../components/price-breakdown/price-breakdown.component";
import { ClientCardComponent } from "../../components/client-card/client-card.component";
import { FullWidthDirective } from "../../directives/full-width.directive";
import { Quote, quoteBooking } from "../../pricing/booking-quote";

import { TPipe } from "../../i18n/t.pipe";
import { opt, tr } from "../../i18n/i18n";
@Component({
    standalone: true,
    selector: "app-bookings-form",
    templateUrl: "./bookings-form.component.html",
    styleUrls: ["./bookings-form.component.css", "./bookings-form.component.less"],
    encapsulation: ViewEncapsulation.None,
    imports: [TPipe, TopNavComponent, Card, FormsModule, ReactiveFormsModule, ButtonDirective, RouterLink, Message, SelectButton, DatePicker, Select, InputText, AutoComplete, PrimeTemplate, Textarea, ClientsFormDetailComponent, ConfirmDialog, Toast, Drawer, OnlyCalendarComponent, TranslatePipe, FormRowComponent, ChildAgesComponent, StatBarComponent, PriceBreakdownComponent, ClientCardComponent, FullWidthDirective]
})
export class BookingsFormComponent
  extends AbstractFormComponent<Bookings>
  implements OnInit
{
  protected fb: FormBuilder;
  private bookingsService = inject(BookingsService);
  private employeesService = inject(EmployeesService);
  translate = inject(TranslateService);
  private roomsService = inject(RoomsService);
  private clientsService = inject(ClientsService);
  private primeng = inject(PrimeNG);
  private platformId = inject(PLATFORM_ID);

  pageTitle: string;
  form: FormGroup;
  blogForm: FormGroup;
  idField = "id";
  readonly filteredClients = signal<any[]>([]);
  formFields = [
    "id",
    "title",
    "description",
    "roomId",
    "name",
    "surname",
    "is_booked",
    "date_from",
    "date_to",
    "final_price",
    "room_price",
    "adults",
    "breakfast_count",
    "breakfast",
    "city",
    "zip",
    "address",
    "email",
    "child_ages",
    "nights",
    "children",
    "is_paid",
    "client_id",
    "works_id",
    "location_id",
    "employee_id",
    "is_active",
  ];
  item$: Observable<Bookings> = of();
  stateOptions = STATE_LIST;
  service = this.bookingsService;
  readonly client = signal<Clients>(undefined);
  singleClient: Clients;
  readonly clientsSearch = signal<Clients[]>(undefined);
  readonly clients = signal<Clients[]>([]);
  selectedClients: Clients;
  capacityNumber: any;
  readonly calendarDialog = signal<boolean>(false);
  readonly selectedWorks = signal<SelectItem[]>([]);
  readonly quote = signal<Quote | null>(null);
  works_id: any;
  readonly employees = signal<Employees[]>(undefined);
  readonly selectedClient = signal<any | null>(null);
  employee: Employees;
  readonly rooms = signal<Rooms[]>(undefined);

  readonly roomOptions = signal<Array<{ id: string; title: string; disabled: boolean }>>([]);
  readonly availabilityHint = signal("");
  readonly availabilityLoading = signal(false);
  private readonly unavailableRoomIds = signal(new Set<string>());
  private readonly lastAvailability = signal<any>(null);
  categories: Category[] = [];
  category: Category;
  works: SelectItem[] = [];
  selectedRooms: SelectItem[] = [];
  readonly selectedRoom = signal<any>(undefined);
  readonly selectedRoomValue = signal<any>(undefined);
  messages1: { severity?: string; detail?: string }[] | undefined;
  showClientForm = false;
  clientDrawer = false;
  currentLang: string;
  readonly booking = signal<Bookings>(undefined);
  bookingsList: Bookings[];
  checkData: any;
  selectedDate: any;
  readonly clientCount = signal<number>(undefined);
  minDateValue = new Date();
  isDateInvalid = true;
  readonly selectedDateFrom = signal<Date | null>(null);
  readonly selectedDateTo = signal<Date | null>(null);
  email: string;
  readonly numberOfNights = signal<number | any>(null);
  readonly breakfastCount = signal<number | any>(null);
  availableRooms: any[] = [];
  availabilityResults: { title: string; isAvailable: boolean }[] = [];
  readonly isLoading = signal<boolean>(false);
  availabilityChecked: boolean = false;
  adults: number;
  children: number;
  roomsAvailable: boolean = false;
  readonly finalPrice = signal<number | any>(null);
  langOptions: any[] = [];
  today: Date;
  adultOptions = NUMBER_LIST;
  childrenOptions = NUMBER_LIST_CHILDREN;
  booking_type = BOOKINGS_TYPE;
  locations = LOCATIONS_LIST;
  requests: any[] = [];
  ageOptions = Array.from({ length: 17 }, (_, i) => ({
    label: (i + 1).toString(),
    value: i + 1,
  }));
  constructor() {
    const fb = inject(FormBuilder);
    const messageService = inject(MessageService);
    const confirmationService = inject(ConfirmationService);
    const router = inject(Router);
    const route = inject(ActivatedRoute);

    super(
      fb,
      messageService,
      confirmationService,
      router,
      route,
      PARAM_BOOKINGS_PATH,
    );
    this.fb = fb;

    this.currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  }

  ngOnInit() {
    this.isLoading.set(true);

    const id = this.route.snapshot.paramMap.get("id");
    const loadBooking$ = id ? this.service.getId(id) : of(null);

    forkJoin({
      employees: this.employeesService.list([]),
      clients: this.clientsService.list([]),
      booking: loadBooking$,
    }).subscribe({
      next: ({ employees, clients, booking }) => {
        this.employees.set(employees);
        this.clients.set(clients);

        if (booking) {
          this.pageTitle = "Modifica Prenotazione";
          this.booking.set(booking);
          this.loadItem(booking.id);
        } else {
          this.pageTitle = "Aggiungi Prenotazione";
        }
        this.initializeForm();

        if (!booking) {
          // a booking made by the staff is usually confirmed and of the most common type: fewer clicks
          this.form.patchValue({ title: BOOKINGS_TYPE[0]?.value ?? "", is_booked: "1" });
        }

        this.isLoading.set(false);
      },
      error: (err) => {
        this.error = err;
        this.isLoading.set(false);
      },
    });
  }

  private formatDateToYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  setCalendarLocale(lang: string) {
    this.translate.get("calendar").subscribe((res) => {
      this.primeng.setTranslation(res);
    });
  }

  onLanguageChange(): void {
    this.translate.use(this.currentLang);
    localStorage.setItem("selectedLang", this.currentLang);
  }

  searchClient(event: { query: string }) {
    const query = (event.query || "").trim();

    if (!query || query.length < 2) {
      this.filteredClients.set([]);
      return;
    }

    const paramsRequests = { _start: 1, _limit: 10, name: query };

    this.clientsService.search(paramsRequests).subscribe((pData) => {
      this.filteredClients.set((pData || []).map((c) => withClientLabel(c)) as any[]);
    });
  }

  onClientSelect(e: any) {
    const client: any = e.value ?? e;

    this.selectedClient.set(withClientLabel(client));

    this.form.patchValue({
      client_id: client.id,
      email: client.email,
      name: client.name,
      surname: client.surname,
      address: client.address,
      city: client.city,
      zip: client.zip,
    });
  }

  onClientClear() {
    this.selectedClient.set(null);
    this.form.get("client_id")?.setValue("");
  }

  bookingCalendar() {
    this.calendarDialog.set(true);
  }

  resetDateFrom(): void {
    this.form.get("date_from")?.reset();
    this.form.get("date_to")?.reset();
    this.form.get("children")?.reset();
    this.form.get("adults")?.reset();

    this.selectedDateFrom.set(null);
    this.selectedDateTo.set(null);
    this.selectedRoom.set("");
  }

  onDateFromChange(date: Date): void {
    this.selectedDateFrom.set(date ? new Date(date) : null);
    this.recalculateTotals();
  }

  onDateToChange(date: Date): void {
    this.selectedDateTo.set(date ? new Date(date) : null);
    this.recalculateTotals();
  }

  recalculateTotals(): void {
    const df = this.form.get("date_from")?.value;
    const dt = this.form.get("date_to")?.value;
    this.selectedDateFrom.set(df ? new Date(df) : this.selectedDateFrom());
    this.selectedDateTo.set(dt ? new Date(dt) : this.selectedDateTo());

    this.calculateFinalPrice();
  }

  calculateFinalPrice(): void {
    const room = this.currentRoom();
    const from = this.selectedDateFrom();
    const to = this.selectedDateTo();
    if (!room || !from || !to) return;

    const nights = Math.max(1, Math.ceil((to.getTime() - from.getTime()) / (1000 * 3600 * 24)));
    this.numberOfNights.set(nights);

    const quote = quoteBooking({
      room,
      adults: this.adultsValue,
      children: this.childrenValue,
      childAges: this.childAges.controls.map((c) => Number(c.value)),
      nights,
      breakfast: this.isBreakfastEnabled(),
    });
    this.quote.set(quote);
    this.form.get("breakfast_count")?.setValue(quote.breakfastCount, { emitEvent: false });
    this.finalPrice.set(quote.total);
  }

  get childAges(): FormArray {
    return this.form.get("childAges") as FormArray;
  }

  readonly stats = computed<Stat[]>(() => {
    const q = this.quote();
    return [
      { label: "Adulti", value: this.adultsValue, icon: "user" },
      { label: "Bambini", value: this.childrenValue, icon: "users" },
      { label: "Notti", value: this.nightsValue(), icon: "moon" },
      { label: "Colazione", value: q?.breakfastCount ?? 0, suffix: "persone", icon: "sun" },
      { label: "Camera", value: q ? q.roomTotal : null, currency: true, icon: "home" },
      { label: "Totale", value: this.finalPrice(), currency: true, icon: "wallet", highlight: true },
    ];
  });

  showViaService() {
    this.messageService.add({
      severity: "success",
      summary: "",
      detail: "Email inviata con successo",
    });
  }

  getFormErrors(): string[] {
    const errors: string[] = [];
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control?.invalid) {
        errors.push(`${key} is required.`);
      }
    });
    return errors;
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) {
      return "";
    }
    return date.toISOString().split("T")[0];
  }

  onSubmit() {
    const dateFrom = new Date(this.form.get("date_from")?.value);
    const dateTo = new Date(this.form.get("date_to")?.value);

    this.form.patchValue({ breakfast_count: this.quote()?.breakfastCount ?? 0 });

    this.form.patchValue({ nights: this.numberOfNights() });
    this.form.get("final_price")?.setValue(this.finalPrice() ?? 0);
    this.form.patchValue({ room_price: this.quote()?.roomPerNight ?? 0 });

    if (!isNaN(dateFrom.getTime())) {
      this.form.patchValue({ date_from: this.formatDateToYYYYMMDD(dateFrom) });
    }
    if (!isNaN(dateTo.getTime())) {
      this.form.patchValue({ date_to: this.formatDateToYYYYMMDD(dateTo) });
    }
    const ages = (this.childAges?.controls ?? []).map((c) =>
      Number(c.value ?? null),
    );
    this.form.patchValue({
      child_ages: JSON.stringify(ages),
    });

    super.onSubmit();
  }

  private readonly fieldLabels: Record<string, string> = {
    title: "Tipo di Vacanza",
    client_id: "Cliente",
    date_from: "Data di arrivo",
    date_to: "Data di partenza",
    roomId: "Camera",
    adults: "Adulti",
    children: "Bambini",
    childAges: "Età bambini",
    breakfast: "Colazione",
    is_booked: "Confermata",
    is_paid: "Pagato",
    is_active: "Attivo",
  };

  invalidFields(): string[] {
    const names = Object.keys(this.form.controls)
      .filter((name) => this.form.get(name)?.invalid)
      .map((name) => tr(this.fieldLabels[name] ?? name));
    if (this.form.get("roomId")?.errors?.roomUnavailable) {
      return names.map((n) => (n === tr("Camera") ? tr("Camera (non disponibile per queste date)") : n));
    }
    return names;
  }

  initializeForm() {
    this.form = this.fb.group({
      id: [""],
      title: ["", Validators.required],
      description: [""],
      is_booked: ["0", Validators.required],
      client_id: ["", Validators.required],
      works_id: [""],
      name: [""],
      city: [""],
      address: [""],
      surname: [""],
      location_id: [""],
      employee_id: [""],
      room_price: [""],
      final_price: [""],
      email: [""],
      is_paid: ["0", Validators.required],
      zip: [""],
      nights: [""],
      is_active: ["1", Validators.required],
      adults: ["2", Validators.required],
      children: ["0", Validators.required],
      breakfast: ["0", Validators.required],
      breakfast_count: [0],
      image: [""],
      date_from: ["", Validators.required],
      date_to: ["", Validators.required],
      roomId: ["", [Validators.required, this.roomAvailableValidator()]],
      child_ages: [""],
      childAges: this.fb.array([]),
    });

    this.form.get("client_id")?.valueChanges.subscribe((clientId) => {
      if (clientId) {
        this.clientsService.getId(clientId).subscribe((pData) => {
          this.client.set(pData);
          (error) => (this.error = error);
        });
      }
    });

    this.form
      .get("adults")
      ?.valueChanges.subscribe(() => this.syncBreakfastCountAndRecalc());
    this.form
      .get("children")
      ?.valueChanges.subscribe(() => this.syncBreakfastCountAndRecalc());
    this.form
      .get("breakfast")
      ?.valueChanges.subscribe(() => this.syncBreakfastCountAndRecalc());
    this.form
      .get("breakfast_count")
      ?.valueChanges.subscribe(() => this.recalculateTotals());

    this.roomsService.list([]).subscribe((pData) => {
      this.rooms.set(pData);
      this.applyAvailability(this.lastAvailability());
      (error) => (this.error = error);
    });

    this.setupAvailability();

    this.form.setValidators(this.roomCapacityValidator());
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private rebuildChildAgesFromBooking(booking: any) {
    const fa = this.childAges;
    fa.clear();

    let ages: any[] = [];
    try {
      ages = booking?.child_ages ? JSON.parse(booking.child_ages) : [];
    } catch {
      ages = [];
    }

    const count = Number(booking?.children ?? 0);
    if (!ages.length && count > 0) {
      ages = new Array(count).fill(null);
    }

    for (let i = 0; i < ages.length; i++) {
      const ctrl = this.fb.control(ages[i], Validators.required);
      ctrl.valueChanges.subscribe(() => this.recalculateTotals());
      fa.push(ctrl);
    }
  }

  checkClient(bookingEmail: string) {
    const clientCheck = { email: bookingEmail };

    this.clientsService.list(clientCheck).subscribe((res) => {
      this.clientsSearch.set(res);
      this.clientCount.set(res.length);

      if (this.clientCount() === 1) {
        this.showConfirmationDialog(this.clientsSearch()[0]);
      }
    });
  }

  private isBreakfastEnabled(): boolean {
    return String(this.form.get("breakfast")?.value ?? "0") === "1";
  }

  showConfirmationDialog(client: Clients) {
    this.confirmationService.confirm({
      message: tr("Abbiamo già trovato un cliente con questo indirizzo email nel tuo database: {name}. Vorresti usare questo?", { name: `${client.name} ${client.surname}` }),
      header: "Attenzione",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.updateBooking(client);
      },
      reject: () => {
        console.log("Update canceled");
      },
    });
  }

  private syncBreakfastCountAndRecalc(): void {
    this.recalculateTotals();
  }

  private roomCapacityValidator() {
    return (group: FormGroup) => {
      const room = this.currentRoom();
      const maxCap = Number(room?.max_capacity ?? 0);

      if (!maxCap) return null;

      const adults = Number(group.get("adults")?.value ?? 0);
      const children = Number(group.get("children")?.value ?? 0);
      const total = adults + children;

      return total > maxCap ? { capacityExceeded: { total, maxCap } } : null;
    };
  }

  onChildrenChange(value: number): void {
    const newCount = Number(value) || 0;
    const fa = this.childAges;

    const existingAges = fa.controls.map((c) => c.value);

    if (newCount > fa.length) {
      for (let i = fa.length; i < newCount; i++) {
        const ctrl = this.fb.control(
          existingAges[i] ?? null,
          Validators.required,
        );
        ctrl.valueChanges.subscribe(() => this.recalculateTotals());
        fa.push(ctrl);
      }
    }

    if (newCount < fa.length) {
      for (let i = fa.length - 1; i >= newCount; i--) {
        fa.removeAt(i);
      }
    }

    this.recalculateTotals();
  }

  onClientUpdated(saved: Clients) {
    this.client.set(saved);
    this.selectedClient.set(withClientLabel(saved));
    this.form.patchValue({
      name: saved.name ?? "",
      surname: saved.surname ?? "",
      email: saved.email ?? "",
      address: saved.address ?? "",
      city: saved.city ?? "",
      zip: saved.zip ?? "",
    });
    this.clientDrawer = false;
  }

  handleNewClient(newClientId: string) {
    this.clientsService.getId(newClientId).subscribe({
      next: (pData) => {
        this.client.set(pData);
        this.onClientSelect(pData);
        this.showClientForm = false;
        if (this.booking()) {
          this.updateBooking(pData);
        }
      },
      error: (error) => (this.error = error),
    });
  }

  updateBooking(client: Clients) {
    const formData = new FormData();

    formData.append("client_id", client.id.toString());
    formData.append("name", client.name);
    formData.append("surname", client.surname);

    this.service.update(formData, this.booking().id).subscribe(
      (res) => {
        if (res.status === "error") {
          this.uploadError = res.message;
        } else {
          console.log("Booking updated successfully!");
          this.loadItem(this.booking().id);
          this.ngOnInit();
        }
      },
      (error) => (this.error = error),
    );
  }

  onRoomChange(event: any): void {
    const roomId = event?.value ?? this.form.get("roomId")?.value;
    if (!roomId) return;

    const room = this.rooms()?.find((r) => r.id === roomId);
    if (!room) return;

    this.selectedRoom.set([room]);


    this.form.updateValueAndValidity({ emitEvent: false });
    this.recalculateTotals();
  }

  onBreakfastToggle(): void {
    this.syncBreakfastCountAndRecalc();
  }

  readonly currentRoom = computed(() => {
    return this.selectedRoom()?.length ? this.selectedRoom()[0] : null;
  });

  get adultsValue(): number {
    return Number(
      this.form.get("adults")?.value ?? this.booking()?.adults ?? 0,
    );
  }
  get childrenValue(): number {
    return Number(
      this.form.get("children")?.value ?? this.booking()?.children ?? 0,
    );
  }
  readonly nightsValue = computed(() => {
    return Number(this.numberOfNights() ?? this.booking()?.nights ?? 0);
  });

  get showBreakfastCount(): boolean {
    return this.form.get("breakfast")?.value === "1";
  }

  private roomAvailableValidator() {
    return (control: AbstractControl): ValidationErrors | null =>
      control.value && this.unavailableRoomIds().has(control.value)
        ? { roomUnavailable: true }
        : null;
  }

  private availabilityParams(): { [key: string]: string | number } | null {
    const from = this.toDate(this.form.get("date_from")?.value);
    const to = this.toDate(this.form.get("date_to")?.value);
    if (!from || !to || to <= from) return null;

    const ages = (this.childAges?.controls ?? [])
      .map((c) => c.value)
      .filter((v) => v !== null && v !== "" && !isNaN(Number(v)));

    return {
      date_from: this.formatDateToYYYYMMDD(from),
      date_to: this.formatDateToYYYYMMDD(to),
      adults: Number(this.form.get("adults")?.value) || 1,
      children: Number(this.form.get("children")?.value) || 0,
      child_ages: ages.join(","),
      breakfast: this.form.get("breakfast")?.value === "1" ? 1 : 0,
      exclude: this.form.get("id")?.value || "",
    };
  }

  private setupAvailability(): void {
    const watched = ["date_from", "date_to", "adults", "children", "breakfast"]
      .map((name) => this.form.get(name)?.valueChanges)
      .filter((stream) => !!stream);

    merge(...(watched as Observable<any>[]))
      .pipe(
        debounceTime(300),
        map(() => this.availabilityParams()),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap((params) => {
          if (!params) return of(null);
          this.availabilityLoading.set(true);
          return this.bookingsService
            .availability(params)
            .pipe(catchError(() => of(null)));
        }),
      )
      .subscribe((result) => this.applyAvailability(result));
  }

  private applyAvailability(result: any): void {
    this.availabilityLoading.set(false);
    this.lastAvailability.set(result);
    const byId: { [id: string]: any } = {};
    (result?.rooms ?? []).forEach((r: any) => (byId[r.id] = r));

    const reasons: { [key: string]: string } = {
      booked: tr("occupata"),
      capacity: tr("troppi ospiti"),
      adults: tr("troppi adulti"),
      children: tr("troppi bambini"),
    };

    this.unavailableRoomIds.set(new Set(
      (result?.rooms ?? []).filter((r: any) => !r.available).map((r: any) => r.id),
    ));

    this.roomOptions.set((this.rooms() ?? []).map((room) => {
      const info = result ? byId[room.id] : null;
      if (!result) return { id: room.id, title: room.title, disabled: false };
      if (!info) return { id: room.id, title: `${room.title} - ${tr("non attiva")}`, disabled: true };
      return info.available
        ? { id: room.id, title: `${room.title} - ${info.quote.total} €`, disabled: false }
        : { id: room.id, title: `${room.title} - ${reasons[info.reason] ?? tr("non disponibile")}`, disabled: true };
    }));

    const free = (result?.rooms ?? []).filter((r: any) => r.available).length;
    this.availabilityHint.set(result
      ? free
        ? tr(result.nights === 1 ? "{free} camere disponibili per {n} notte" : "{free} camere disponibili per {n} notti", { free, n: result.nights })
        : tr("Nessuna camera disponibile per queste date e questi ospiti")
      : "");

    this.form?.get("roomId")?.updateValueAndValidity();
  }

  private asOption(value: any, fallback: string): string {
    if (value === null || value === undefined || value === "") return fallback;
    if (value === true) return "1";
    if (value === false) return "0";
    return String(value);
  }

  private toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    const date = new Date(String(value).replace(" ", "T"));
    return isNaN(date.getTime()) ? null : date;
  }

  loadItem(id: string): void {
    this.service
      .getId(id)
      .pipe(
        tap((booking) => {
          this.booking.set(booking);

          const clientId = booking.client_id;
          if (!clientId) {
            this.checkClient(booking.email);
          }
        }),

        switchMap((booking) => {
          const room$ = booking.roomId
            ? this.roomsService.getId(booking.roomId)
            : of(null);

          const client$ = booking.client_id
            ? this.clientsService.getId(booking.client_id)
            : of(null);

          return forkJoin({ room: room$, client: client$ }).pipe(
            map(({ room, client }) => ({ booking, room, client })),
          );
        }),

        catchError((error) => {
          this.error = error;
          console.error("Error loading item:", error);
          return of(null);
        }),
      )
      .subscribe((data) => {
        if (!data) return;

        const { booking, room, client } = data;

        this.selectedRoomValue.set(room);
        this.selectedRoom.set(room ? [room] : []);
        this.selectedClient.set(withClientLabel(booking.client?.id ? booking.client : client));

        if (this.selectedClient()) {
          this.filteredClients.set([this.selectedClient()]);
        }

        this.form.patchValue({
          id: booking.id,
          title: booking.title,
          description: booking.description,
          client_id: booking.client_id || "",
          works_id: booking.works_id,
          employee_id: booking.employee_id,
          location_id: booking.location_id,
          date_from: booking.date_from,
          date_to: booking.date_to,
          is_booked: this.asOption(booking.is_booked, "0"),
          adults: this.asOption(booking.adults, "1"),
          children: this.asOption(booking.children, "0"),
          breakfast: this.asOption(booking.breakfast, "0"),
          is_active: this.asOption(booking.is_active, "1"),
          final_price: booking.final_price,
          is_paid: this.asOption(booking.is_paid, "0"),
          roomId: booking.roomId || (booking as any).room_id || "",
          nights: booking.nights,
          breakfast_count: booking.breakfast_count,
          room_price: booking.room_price,
          name: client?.name ?? "",
          surname: client?.surname ?? "",
          email: client?.email ?? booking.email ?? "",
          city: client?.city ?? "",
          address: client?.address ?? "",
          zip: client?.zip ?? "",
        });

        this.selectedDateFrom.set(this.toDate(booking.date_from));
        this.selectedDateTo.set(this.toDate(booking.date_to));

        this.rebuildChildAgesFromBooking(booking);
        this.recalculateTotals();

        this.selectedWorks.set(this.form.get("works_id")?.value ?? []);
        this.numberOfNights.set(booking.nights);
        this.breakfastCount.set(booking.breakfast_count);

        this.finalPrice.set(booking.final_price);

      });
  }
}
